// ========================================
// Tutorías Admin & RRHH View
// El mes elegido arriba; a la izquierda el registro de todas las tutorías (buscador y estados),
// a la derecha las horas del mes por docente para marcar qué va a la asesoría (nóminas).
// ========================================

const TutoriasAdminView = {
    _stats: null,
    _selectedMonth: null,
    searchQuery: '',
    statusFilter: 'all',

    MONTHS: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    STATES: {
        confirmed: ['Confirmada', 'is-confirmed'],
        pending: ['Pendiente', 'is-pending'],
        negotiating: ['En negociación', 'is-negotiating'],
        cancelled: ['Cancelada', 'is-cancelled']
    },

    monthString() {
        return this._stats?.currentMonthString || this._selectedMonth || new Date().toISOString().substring(0, 7);
    },
    monthLabel(short = false) {
        const month = this.monthString(), index = parseInt(month.substring(5, 7), 10) - 1;
        if (isNaN(index)) return 'Mes actual';
        return short ? this.MONTHS[index].slice(0, 3).toLowerCase() : `${this.MONTHS[index]} ${month.substring(0, 4)}`;
    },

    /** Each professor with confirmed tutorías in the month: hours (one per tutoría) and whether they went to the advisory. */
    monthProfessors() {
        const month = this.monthString(), byProfessor = {};
        for (const t of this._stats?.tutorias || []) {
            if (t.status !== 'confirmed' || !t.confirmedDate?.startsWith(month)) continue;
            const p = byProfessor[t.professorId] ||= { id: t.professorId, name: t.professorName || 'Sin nombre', count: 0, sent: true };
            p.count++;
            if (!t.sentToAdvisory) p.sent = false;
        }
        return Object.values(byProfessor).sort((a, b) => (a.sent - b.sent) || b.count - a.count || a.name.localeCompare(b.name, 'es'));
    },

    render() {
        if (!this._stats) {
            return `
            <div class="view-container dnr-proposal tu" style="text-align: center; padding: 4rem 1rem;">
                <i class="fas fa-spinner fa-spin" style="font-size: 2rem; color: var(--dnr-teal-600); margin-bottom: 1rem;"></i>
                <p style="color: var(--dnr-ink-mute); font-weight: 600;">Cargando las tutorías…</p>
            </div>`;
        }
        return `
        <div class="view-container dnr-proposal tu" id="tutorias-page">
            <div class="tu-head">
                <div class="tu-head-text">
                    <span class="tu-mono">Personal y RRHH · Tutorías</span>
                    <h1 class="tu-title">Control de tutorías</h1>
                </div>
                <div class="tu-month" role="group" aria-label="Mes">
                    <button type="button" class="tu-month-btn" aria-label="Mes anterior" ${UIEvents.attr("click", function(event,captured){TutoriasAdminView.changeMonth(-1);}, [])}><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
                    <label class="tu-month-now" title="Elegir otro mes">
                        <i class="far fa-calendar-alt" aria-hidden="true"></i><span id="tu-month-label">${this.monthLabel()}</span>
                        <input type="month" class="tu-month-input" aria-label="Elegir mes" value="${this.monthString()}" ${UIEvents.attr("change", function(event,captured){TutoriasAdminView.onMonthChange(this.value);}, [])}>
                    </label>
                    <button type="button" class="tu-month-btn" aria-label="Mes siguiente" ${UIEvents.attr("click", function(event,captured){TutoriasAdminView.changeMonth(1);}, [])}><i class="fas fa-chevron-right" aria-hidden="true"></i></button>
                </div>
            </div>
            <div class="tu-stats" id="tu-stats">${this.renderStats()}</div>
            <div class="tu-layout">
                <section class="tu-card tu-log" aria-labelledby="tu-log-title">
                    <div class="tu-card-head">
                        <h2 class="tu-h2" id="tu-log-title">Registro</h2>
                        <label class="tu-search">
                            <i class="fas fa-search" aria-hidden="true"></i>
                            <input type="search" id="ta-search-input" placeholder="Alumno, docente o motivo" aria-label="Buscar alumno, docente o motivo" value="${Utils.sanitizeHTML(this.searchQuery || '')}"
                                ${UIEvents.attr("input", function(event,captured){TutoriasAdminView.setSearch(this.value);}, [])}>
                        </label>
                    </div>
                    <div class="tu-filters" role="group" aria-label="Estado" id="tu-filters"></div>
                    <div class="tu-scroll">
                        <table class="tu-table">
                            <thead><tr>
                                <th scope="col">Alumno → docente</th>
                                <th scope="col">Cuándo</th>
                                <th scope="col">Estado</th>
                                <th scope="col">Motivo</th>
                            </tr></thead>
                            <tbody id="ta-requests-body"></tbody>
                        </table>
                    </div>
                </section>
                <aside class="tu-aside">
                    <section class="tu-card tu-pad" aria-labelledby="tu-adv-title" id="tu-advisory">${this.renderAdvisory()}</section>
                    <section class="tu-card tu-pad tu-note">
                        <strong>Cómo se cuentan</strong>
                        <span>Cada tutoría confirmada suma una hora al docente en el mes de su fecha. Las pendientes, en negociación o canceladas no cuentan.</span>
                    </section>
                </aside>
            </div>
        </div>`;
    },

    renderStats() {
        const all = this._stats?.tutorias || [], profs = this.monthProfessors();
        const sent = profs.filter(p => p.sent).length, left = profs.length - sent;
        const pending = all.filter(t => t.status === 'pending').length, negotiating = all.filter(t => t.status === 'negotiating').length;
        const confirmed = all.filter(t => t.status === 'confirmed').length;
        const stat = (label, value, sub, extra = '') => `<div class="tu-stat ${extra}"><span class="tu-stat-label">${label}</span><span class="tu-stat-value">${value}</span><span class="tu-stat-sub">${sub}</span></div>`;
        return [
            stat(`Horas de tutoría · ${this.monthLabel(true)}`, `${this._stats.currentMonthCount || 0} h`, 'Una hora por tutoría confirmada'),
            stat('Asesoría · nóminas', profs.length ? `${sent} <small>de ${profs.length} enviados</small>` : '—', profs.length ? (left ? `Faltan ${left} por enviar` : 'Todo enviado') : 'Sin tutorías este mes', left ? 'is-warn' : profs.length ? 'is-ok' : ''),
            stat('Por acordar', pending + negotiating, `${pending} ${pending === 1 ? 'pendiente' : 'pendientes'} · ${negotiating} en negociación`),
            stat('Histórico', `${confirmed} <small>confirmadas</small>`, `de ${all.length} ${all.length === 1 ? 'solicitud' : 'solicitudes'}`)
        ].join('');
    },

    renderAdvisory() {
        const profs = this.monthProfessors(), sent = profs.filter(p => p.sent).length;
        const canManage = AuthManager.canManageModule ? AuthManager.canManageModule('tutorias-admin', ['admin', 'coordinator', 'rrhh']) : true;
        const initials = name => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'P';
        return `
            <div class="tu-row-between">
                <h2 class="tu-h2 is-small" id="tu-adv-title">Horas para la asesoría</h2>
                <span class="tu-mono">${this.monthLabel(true)}</span>
            </div>
            <span class="tu-hint">Tutorías confirmadas del mes por docente. Marca cada uno cuando sus horas vayan a la asesoría para la nómina.</span>
            ${profs.length ? `
            <span class="tu-progress" role="img" aria-label="${sent} de ${profs.length} docentes enviados"><span style="width: ${Math.round(sent / profs.length * 100)}%"></span></span>
            <ul class="tu-profs">${profs.map(p => `<li>
                <span class="tu-avatar" aria-hidden="true">${Utils.sanitizeHTML(initials(p.name))}</span>
                <span class="tu-prof-text"><strong>${Utils.sanitizeHTML(p.name)}</strong><span>${p.count} h · ${p.count} ${p.count === 1 ? 'tutoría' : 'tutorías'}</span></span>
                <button type="button" class="tu-send ${p.sent ? 'is-sent' : ''}" aria-pressed="${p.sent}" ${canManage ? '' : 'disabled'}
                    title="${p.sent ? 'Enviado a la asesoría. Pulsa para dejarlo pendiente.' : 'Marcar sus horas del mes como enviadas a la asesoría'}"
                    ${UIEvents.attr("click", function(event,captured){TutoriasAdminView.toggleRegularization(String(captured[0]),String(captured[1]),captured[2]==='1',this);}, [p.id, this.monthString(), p.sent ? '0' : '1'])}>${p.sent ? '<i class="fas fa-check" aria-hidden="true"></i> Enviado' : 'Marcar enviado'}</button>
            </li>`).join('')}</ul>` : `<p class="tu-empty-small">No hay tutorías confirmadas en ${this.monthLabel()}.</p>`}`;
    },

    renderFilters() {
        const box = document.getElementById('tu-filters');
        if (!box) return;
        const all = this._stats?.tutorias || [], counts = { all: all.length };
        for (const t of all) counts[t.status] = (counts[t.status] || 0) + 1;
        SafeHTML.set(box, [['all', 'Todas'], ['pending', 'Pendientes'], ['negotiating', 'En negociación'], ['confirmed', 'Confirmadas'], ['cancelled', 'Canceladas']].map(([key, label]) =>
            `<button type="button" class="tu-filter ${this.statusFilter === key ? 'is-on' : ''}" aria-pressed="${this.statusFilter === key}" ${UIEvents.attr("click", function(event,captured){TutoriasAdminView.setStatusFilter(String(captured[0]));}, [key])}>${label} · ${counts[key] || 0}</button>`).join(''));
    },

    getFilteredTutorias() {
        if (!this._stats || !this._stats.tutorias) return [];
        const query = (this.searchQuery || '').toLowerCase().trim();
        return this._stats.tutorias.filter(t => {
            if (this.statusFilter !== 'all' && t.status !== this.statusFilter) {
                return false;
            }
            if (query) {
                const sName = (t.studentName || '').toLowerCase();
                const pName = (t.professorName || '').toLowerCase();
                const notes = (t.requestNotes || '').toLowerCase();
                const reason = (t.rejectionReason || t.cancellationReason || '').toLowerCase();
                if (!sName.includes(query) && !pName.includes(query) && !notes.includes(query) && !reason.includes(query)) {
                    return false;
                }
            }
            return true;
        });
    },

    renderRequestsTable() {
        const tbody = document.getElementById('ta-requests-body');
        if (!tbody) return;
        this.renderFilters();
        const filtered = this.getFilteredTutorias();
        const title = document.getElementById('tu-log-title');
        if (title) title.textContent = `Registro · ${filtered.length}`;

        if (filtered.length === 0) {
            SafeHTML.set(tbody, `<tr><td colspan="4" class="tu-empty">No hay tutorías con estos filtros.</td></tr>`);
            return;
        }

        const day = iso => { const [y, m, d] = String(iso).split('-'); return d && m ? `${d}/${m}${y !== this.monthString().substring(0, 4) ? `/${y.slice(2)}` : ''}` : iso; };
        SafeHTML.set(tbody, filtered.map(t => {
            const [label, cls] = this.STATES[t.status] || this.STATES.pending;
            const confirmed = t.confirmedDate && t.confirmedTime;
            const when = confirmed ? `${day(t.confirmedDate)} · ${t.confirmedTime}` : t.proposedDate ? `${day(t.proposedDate)}${t.proposedTime ? ` · ${t.proposedTime}` : ''}` : 'Por acordar';
            const whenNote = confirmed ? 'confirmada' : t.proposedDate ? 'propuesta' : 'sin fecha';
            const reason = t.status === 'cancelled' ? (t.cancellationReason || t.rejectionReason || '') : '';
            const notes = t.requestNotes || '';
            return `
            <tr>
                <td><span class="tu-who"><strong>${Utils.sanitizeHTML(t.studentName || 'Sin nombre')}</strong><span>con ${Utils.sanitizeHTML(t.professorName || 'sin docente')}</span></span></td>
                <td><span class="tu-when"><strong>${Utils.sanitizeHTML(when)}</strong><span>${whenNote}</span></span></td>
                <td><span class="tu-pill ${cls}">${label}</span></td>
                <td><span class="tu-notes" title="${Utils.sanitizeHTML([notes, reason].filter(Boolean).join(' · '))}">${notes ? Utils.sanitizeHTML(notes) : '<em>Sin notas</em>'}${reason ? `<small>${Utils.sanitizeHTML(reason)}</small>` : ''}</span></td>
            </tr>`;
        }).join(''));
    },

    setSearch(value) {
        this.searchQuery = value || '';
        this.renderRequestsTable();
    },

    setStatusFilter(value) {
        this.statusFilter = value || 'all';
        this.renderRequestsTable();
    },

    async init() {
        await this.loadStats();
        const contentArea = document.getElementById('content-area');
        if (contentArea) {
            SafeHTML.set(contentArea, this.render());
            this.renderRequestsTable();
        }
    },

    /** After a month change or a mark: the figures, the advisory column and the table, not the whole page. */
    refreshParts() {
        if (!document.getElementById('tutorias-page')) return this.init();
        const label = document.getElementById('tu-month-label'), input = document.querySelector('#tutorias-page .tu-month-input');
        if (label) label.textContent = this.monthLabel();
        if (input) input.value = this.monthString();
        SafeHTML.set(document.getElementById('tu-stats'), this.renderStats());
        SafeHTML.set(document.getElementById('tu-advisory'), this.renderAdvisory());
        this.renderRequestsTable();
    },

    async loadStats() {
        try {
            if (!this._selectedMonth) {
                this._selectedMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
            }
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/tutorias/stats?month=${this._selectedMonth}`, {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await res.json();
            if (data.success) {
                this._stats = data.stats;
            }
        } catch(e) {
            console.error('Error al cargar estadísticas:', e);
        }
    },

    async onMonthChange(monthValue) {
        if (!monthValue) return;
        this._selectedMonth = monthValue;
        await this.loadStats();
        this.refreshParts();
    },

    async changeMonth(offset) {
        if (!this._selectedMonth) {
            this._selectedMonth = new Date().toISOString().substring(0, 7);
        }
        const date = new Date(this._selectedMonth + '-15');
        date.setMonth(date.getMonth() + offset);
        this._selectedMonth = date.toISOString().substring(0, 7);
        await this.loadStats();
        this.refreshParts();
    },

    async toggleRegularization(professorId, month, sent, button) {
        if (!AuthManager.requireModuleManage("tutorias-admin",["admin","coordinator","rrhh"])) return;
        if (button?.disabled) return;
        if (button) button.disabled = true;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tutorias/regularizar', {
                method: 'POST',
                headers: {
                    'X-SICAF-CSRF': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ professorId, month, sent })
            });

            const data = await res.json();
            if (data.success) {
                Utils.showToast(sent ? 'Marcado como enviado a asesoría' : 'Marcado como pendiente', 'success');
                await this.loadStats();
                this.refreshParts();
            } else {
                SafeHTML.fire('Error', data.error || 'No se pudo actualizar el estado', 'error');
                if (button) button.disabled = false;
            }
        } catch(e) {
            console.error('Error regularizar:', e);
            if (button) button.disabled = false;
        }
    }
};

window.TutoriasAdminView = TutoriasAdminView;
